import React from "react";
import { injectIntl } from 'react-intl';
import { Avatar, Tooltip } from "antd";
import { ExclamationCircleFilled, LoadingOutlined, PaperClipOutlined, TeamOutlined } from '@ant-design/icons';
import connect from "react-redux/es/connect/connect";
import { discardFailedMessage, sendMessage } from "../../../../appRedux/actions";
import { formatPhoneNumberForDisplay, formatTime, messageTime, matchUser } from "../../../../util/helpers";
import IntlMessages from "../../../../util/IntlMessages";
import { countryNames } from "../../../../util/countryNames";
import { Platform } from "../../../../util/platform";
import whatsappIcon from "../../../../assets/images/whatsapp.svg";

const SentMessageCell = ({message, users, intl, phoneNumbers, sendMessage, discardFailedMessage}) => {
    const user = matchUser(message.userId, users);
    // A message still being sent (or that failed) only has the number it goes from; show the
    // sender name for an alpha sender, never the technical number behind it.
    const senderName = message.customName
        || (phoneNumbers || []).find(number => number.number === message.from)?.customName;
    const failedText = message.failed && intl.formatMessage({
        id: `errorMessage.${message.error || 'ERROR_MESSAGE'}`,
        defaultMessage: 'Could not send the message.'
    }, {countries: countryNames(intl, message.errorCountries)});
    const retry = () => {
        if (!message.retry) return;
        const {message: body, recipientNumber, senderNumber, channel, mmsFile} = message.retry;
        discardFailedMessage(message.clientNumber, message.tempId);
        sendMessage(body, recipientNumber, senderNumber, channel, mmsFile);
    };

    const getFormattedStatus = () => {
        if (message.pending) {
            return <span className="out-message-status__sending">{intl.formatMessage({id: 'messageStatus.SENDING', defaultMessage: 'Sending…'})}</span>;
        }
        if (message.failed) {
            return <span className="out-message-status__failed">{intl.formatMessage({id: 'messageStatus.NOT_SENT', defaultMessage: 'Not sent'})}</span>;
        }
        if (message.last) {
            switch (message.status) {
                case 'DELIVERED':
                case 'READ':
                case 'PENDING':
                case 'PROCESSING':
                case 'CARRIER_PENDING':
                    return <IntlMessages id={`messageStatus.${message.status}`}/>;
                case 'SENDING_RESTRICTED':
                    return <span className="out-message-status__failed"><IntlMessages id="messageStatus.SENDING_RESTRICTED"/></span>;
                case 'UNDELIVERED':
                case 'BILLING_ERROR':
                    return <>
                        <IntlMessages id={`messageStatus.${message.status}`}/>
                        {/*<strong className="retry-message">| <IntlMessages id="message.tryAgain"/></strong>*/}
                    </>;
                default:
                    return <IntlMessages id='messageStatus.SENT'/>;
            }
        }

        switch (message.status) {
            case 'SENDING_RESTRICTED':
                return <span className="out-message-status__failed"><IntlMessages id="messageStatus.SENDING_RESTRICTED"/></span>;
            case 'UNDELIVERED':
            case 'BILLING_ERROR':
                return <>
                    <IntlMessages id={`messageStatus.${message.status}`}/>
                    <strong className="retry-message">| <IntlMessages id="message.tryAgain"/></strong>
                </>;
            default:
                return '';
        }
    };

    return (
        <div className={`gx-chat-out-item-wrapper ${message.pending ? 'is-sending' : ''} ${message.failed ? 'is-failed' : ''}`} aria-busy={message.pending ? 'true' : undefined}>
            <div className="gx-chat-item gx-flex-row-reverse">
                <p className='avatar-container'>
                    <Avatar className="avatar-circle circle-flex-container"
                            src={user && user.picture}
                            alt={message.from}
                    />
                </p>
                <div className="gx-bubble-block">
                        {message.pending && <LoadingOutlined className="message-sending-icon" aria-hidden="true"/>}
                        {message.failed && (
                            <Tooltip title={`${failedText} ${intl.formatMessage({id: 'message.clickToRetry', defaultMessage: 'Click to try again.'})}`}>
                                <button type="button" className="message-failed-icon" onClick={retry}
                                        aria-label={`${failedText} ${intl.formatMessage({id: 'message.clickToRetry', defaultMessage: 'Click to try again.'})}`}>
                                    <ExclamationCircleFilled/>
                                </button>
                            </Tooltip>
                        )}
                        <div className="gx-bubble" title={formatTime(message.time)}>
                            <div className="gx-message">{message.body}</div>
                        </div>
                </div>
            </div>
            <span  className="bubble-from outgoing-message-info">
                {message.properties?.attachments?.length && (
                    <a href={message.properties.attachments[0].fileUrl} target="_blank" rel="noopener noreferrer" className="attachment">
                        <PaperClipOutlined title={intl.formatMessage({id: 'message.downloadAttachment'})} />
                    </a>
                )}
                <IntlMessages id="from"/>: {senderName || formatPhoneNumberForDisplay(message.from)}
                {message.platform === Platform.whatsapp && <img src={whatsappIcon} alt="WhatsApp" className="whatsapp-icon"/>}
                <br/>
            </span>
            <span className="bubble-time outgoing-message-info">
                {messageTime(message.time)} {message.isBulk && (<TeamOutlined title={intl.formatMessage({id: 'bulkMessage'})} />)}<br/>
            </span>
            <span className="out-message-status">
                {(message.status || message.pending || message.failed) &&
                    getFormattedStatus()
                }
            </span>
        </div>
    )
};

const mapStateToProps = state => ({phoneNumbers: state.accounts?.phoneNumbersAll});

export default connect(mapStateToProps, {sendMessage, discardFailedMessage})(injectIntl(SentMessageCell));
